import { MapPin, MapPinned } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { categoryById } from "../data/categories";
import type { Place, RegionId } from "../data/schema";
import { priceLabels, regionLabels, stayTimeLabels } from "../lib/labels";

interface WeeklyRecommendationsProps {
  places: Place[];
  onSelect: (place: Place) => void;
}

export function WeeklyRecommendations({ places, onSelect }: WeeklyRecommendationsProps) {
  const availableRegions = useMemo(
    () => Array.from(new Set(places.map((place) => place.region))),
    [places],
  );
  const [activeRegion, setActiveRegion] = useState<RegionId>(availableRegions[0] ?? "seoul");
  const regionalTopPlaces = useMemo(
    () =>
      Object.fromEntries(
        availableRegions.map((region) => [
          region,
          places.filter((place) => place.region === region).sort((a, b) => a.name.localeCompare(b.name, "ko")).slice(0, 10),
        ]),
      ) as Partial<Record<RegionId, Place[]>>,
    [availableRegions, places],
  );
  const activePlaces = regionalTopPlaces[activeRegion] ?? [];

  useEffect(() => {
    if (!availableRegions.includes(activeRegion) && availableRegions[0]) {
      setActiveRegion(availableRegions[0]);
    }
  }, [activeRegion, availableRegions]);

  return (
    <section className="weekly-section" aria-label="지역별 둘러보기">
      <div className="section-heading">
        <MapPinned size={20} aria-hidden="true" />
        <div>
          <h2>지역별 둘러보기</h2>
          <p>지역별 여행지를 이름순으로 모았습니다.</p>
        </div>
        <span>{activePlaces.length}곳</span>
      </div>

      <div className="region-tabs" role="tablist" aria-label="둘러볼 지역">
        {availableRegions.map((region) => (
          <button
            aria-selected={activeRegion === region}
            className={activeRegion === region ? "active" : ""}
            key={region}
            onClick={() => setActiveRegion(region)}
            role="tab"
            type="button"
          >
            <strong>{regionLabels[region]}</strong>
            <span>{regionalTopPlaces[region]?.length ?? 0}곳</span>
          </button>
        ))}
      </div>

      <div className="weekly-detail" role="tabpanel">
        <div className="weekly-detail-heading">
          <strong>{regionLabels[activeRegion]}</strong>
          <span>{activePlaces.length > 0 ? "이름순 · 최대 10곳" : "등록된 여행지가 없습니다."}</span>
        </div>

        <div className="weekly-place-list">
          {activePlaces.map((place, index) => (
            <button key={place.id} onClick={() => onSelect(place)} type="button">
              <span className="rank-badge">{index + 1}</span>
              <span className="weekly-place-copy">
                <strong>{place.name}</strong>
                <span>
                  <MapPin size={13} aria-hidden="true" />
                  {place.district} · {categoryById[place.category].name} ·{" "}
                  {priceLabels[place.priceType]} · {stayTimeLabels[place.stayTime]}
                </span>
              </span>

            </button>
          ))}
          {activePlaces.length === 0 && <div className="empty">등록된 여행지가 없습니다.</div>}
        </div>
      </div>
    </section>
  );
}
